// PLAN-CAMPO (2026-06-15) — Planejamento Semanal de Campo.
// Calendário semanal: cada "campanha" é um time ad-hoc + equipamentos alocados
// a um contrato num período, com localidades e briefing próprios. Fecha na
// sexta e é repassado à equipe de campo (link público + Telegram — fases 2/3).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Button, DatePicker, Divider, Drawer, Empty, Flex, Form, Input,
  Select, Space, Spin, Typography,
} from 'antd';
import {
  PlusOutlined, LeftOutlined, RightOutlined, DeleteOutlined, EnvironmentOutlined,
  DownloadOutlined, UploadOutlined, ShareAltOutlined, CopyOutlined, CheckOutlined, SendOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';

const { Text, Title } = Typography;
const { TextArea } = Input;

const STATUS_CFG: Record<string, { label: string; cor: string; hex: string }> = {
  planejada:  { label: 'Planejada',  cor: 'blue',    hex: '#1677ff' },
  confirmada: { label: 'Confirmada', cor: 'gold',    hex: '#B07500' },
  enviada:    { label: 'Enviada',    cor: 'purple',  hex: '#722ed1' },
  concluida:  { label: 'Concluída',  cor: 'success', hex: '#389e0d' },
  cancelada:  { label: 'Cancelada',  cor: 'default', hex: '#999' },
};
const DIAS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

interface Campanha {
  id: string;
  contrato_id: string | null;
  contrato_numero: string | null;
  contrato_titulo: string | null;
  titulo: string | null;
  data_inicio: string;
  data_fim: string;
  status: string;
  briefing: string | null;
  observacoes: string | null;
  profissionais: string[];
  equipamentos: string[];
  kits?: Array<{ kit_id: string; nome: string; codigo: string | null }>;
  localidades: Array<{ nome?: string; link?: string; obs?: string }>;
  arquivos?: Array<{ nome: string; s3_key: string; tipo: string }>;
  token_hash?: string | null;
}

interface KitOpcao { id: string; nome: string; codigo: string | null; status: string; }

// Segunda-feira da semana de uma data
function segundaDe(d: Dayjs): Dayjs {
  const dow = d.day(); // 0=Dom..6=Sáb
  return d.subtract((dow + 6) % 7, 'day').startOf('day');
}

export default function PlanejamentoCampo() {
  return <AntApp><PlanejamentoCampoInner /></AntApp>;
}

function PlanejamentoCampoInner() {
  const { message, modal } = AntApp.useApp();
  const [semana, setSemana] = useState<Dayjs>(() => segundaDe(dayjs()));
  const [campanhas, setCampanhas] = useState<Campanha[]>([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState<Campanha | null>(null);
  const [aberto, setAberto] = useState(false);
  const [enviando, setEnviando] = useState(false);

  // Catálogos pro modal
  const [contratos, setContratos] = useState<Array<{ id: string; numero: string; titulo: string }>>([]);
  const [pessoas, setPessoas] = useState<string[]>([]);
  const [equipamentos, setEquipamentos] = useState<Array<{ descricao: string; codigo_patrimonio: string | null; categoria: string | null; modelo: string | null }>>([]);
  const [kitsDisp, setKitsDisp] = useState<KitOpcao[]>([]);

  const diasSemana = useMemo(() => Array.from({ length: 7 }, (_, i) => semana.add(i, 'day')), [semana]);
  const fimSemana = diasSemana[6];

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_pc_listar', semana_inicio: semana.format('YYYY-MM-DD') },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setCampanhas(Array.isArray(d) ? d : []);
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [semana, message]);
  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const [rc, ru, re, rk] = await Promise.allSettled([
          axios.get(`${API_URL}/mobile/contratos`, { headers: { Authorization: token } }),
          axios.post(`${API_URL}/comercial/propostas-geradas`, { action: 'op_usuario_dados_tecnicos_listar_cognito' }, { headers: { Authorization: token } }),
          axios.post(`${API_URL}/comercial/propostas-geradas`, { action: 'adm_equipamentos_alocaveis' }, { headers: { Authorization: token } }),
          axios.post(`${API_URL}/comercial/propostas-geradas`, { action: 'op_kit_listar' }, { headers: { Authorization: token } }),
        ]);
        if (rc.status === 'fulfilled') { const d = rc.value.data.body ? JSON.parse(rc.value.data.body) : rc.value.data; setContratos(Array.isArray(d) ? d : (d.contratos || [])); }
        const nomes = new Set<string>();
        if (ru.status === 'fulfilled') { const d = ru.value.data.body ? JSON.parse(ru.value.data.body) : ru.value.data; for (const u of (Array.isArray(d) ? d : [])) { if (u.nome) nomes.add(u.nome); else if (u.email) nomes.add(u.email.split('@')[0]); } }
        setPessoas(Array.from(nomes).sort((a, b) => a.localeCompare(b)));
        if (re.status === 'fulfilled') { const d = re.value.data.body ? JSON.parse(re.value.data.body) : re.value.data; setEquipamentos(Array.isArray(d) ? d : []); }
        if (rk.status === 'fulfilled') { const d = rk.value.data.body ? JSON.parse(rk.value.data.body) : rk.value.data; setKitsDisp((Array.isArray(d) ? d : []).filter((k: KitOpcao) => k.status !== 'inativo')); }
      } catch { /* opcional */ }
    })();
  }, []);

  function abrirNova() { setEditando(null); setAberto(true); }
  function abrirEdicao(c: Campanha) { setEditando(c); setAberto(true); }

  async function excluir(c: Campanha) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_pc_excluir', id: c.id }, { headers: { Authorization: token } });
      message.success('Campanha excluída.');
      carregar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    }
  }

  // Fecha a semana e manda o consolidado no Telegram (grupo de campo)
  function fecharEnviar() {
    const ativas = campanhas.filter(c => c.status !== 'cancelada');
    if (!ativas.length) { message.warning('Nenhuma campanha nesta semana pra enviar.'); return; }
    modal.confirm({
      title: 'Fechar e enviar pro grupo de campo (Telegram)?',
      content: `${ativas.length} campanha(s) da semana de ${semana.format('DD/MM')} a ${fimSemana.format('DD/MM')} serão enviadas ao grupo "Diários de Campo" com o link de cada uma.`,
      okText: 'Enviar agora', icon: <SendOutlined />,
      onOk: async () => {
        setEnviando(true);
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_pc_fechar_enviar', semana_inicio: semana.format('YYYY-MM-DD') },
            { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          message.success(`Enviado ao grupo de campo: ${d.total_campanhas} campanha(s).`, 5);
          carregar();
        } catch (e: any) {
          message.error('Falha no envio: ' + (e.response?.data?.error || e.message));
        } finally { setEnviando(false); }
      },
    });
  }

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 6 }}>
        <Title level={4} style={{ margin: 0 }}>📅 Planejamento de Campo</Title>
        <Space>
          <Button icon={<LeftOutlined />} onClick={() => setSemana(s => s.subtract(7, 'day'))} />
          <Button onClick={() => setSemana(segundaDe(dayjs()))}>Esta semana</Button>
          <Button icon={<RightOutlined />} onClick={() => setSemana(s => s.add(7, 'day'))} />
          <Button icon={<SendOutlined />} loading={enviando} onClick={fecharEnviar}
            style={{ borderColor: '#0a7f4a', color: '#0a7f4a' }}>
            Fechar e enviar
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={abrirNova}>Nova campanha</Button>
        </Space>
      </Flex>
      <Text type="secondary" style={{ display: 'block', marginBottom: 14, fontSize: 13 }}>
        Semana de <strong>{semana.format('DD/MM')}</strong> a <strong>{fimSemana.format('DD/MM/YYYY')}</strong>
      </Text>

      {/* Cabeçalho dos dias */}
      <div style={{ display: 'grid', gridTemplateColumns: '260px repeat(7, 1fr)', gap: 1,
        background: '#e8e8e8', border: '1px solid #e8e8e8', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ background: '#F0F1F5', padding: '8px 10px', fontSize: 12, fontWeight: 600, color: '#555' }}>Campanha</div>
        {diasSemana.map((d, i) => (
          <div key={i} style={{ background: '#F0F1F5', padding: '8px 6px', textAlign: 'center', fontSize: 11, fontWeight: 600,
            color: d.isSame(dayjs(), 'day') ? '#0930D0' : '#555' }}>
            {DIAS[i]}<br /><span style={{ fontSize: 10, fontWeight: 400 }}>{d.format('DD/MM')}</span>
          </div>
        ))}

        {/* Linhas de campanhas */}
        {loading ? (
          <div style={{ gridColumn: '1 / -1', background: '#fff', padding: 40, textAlign: 'center' }}><Spin /></div>
        ) : campanhas.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', background: '#fff', padding: 30 }}>
            <Empty description="Nenhuma campanha nesta semana. Clique em 'Nova campanha'." />
          </div>
        ) : campanhas.map(c => {
          // timezone-safe: backend devolve DATE como ISO UTC meia-noite; fatia o
          // yyyy-mm-dd antes do dayjs senão em BRT (UTC-3) cai no dia anterior.
          const ini = dayjs(String(c.data_inicio).slice(0, 10)), fim = dayjs(String(c.data_fim).slice(0, 10));
          const startIdx = Math.max(0, ini.diff(semana, 'day'));
          const endIdx = Math.min(6, fim.diff(semana, 'day'));
          const cfg = STATUS_CFG[c.status] || STATUS_CFG.planejada;
          return (
            <div key={c.id} style={{ display: 'contents' }}>
              <div onClick={() => abrirEdicao(c)}
                style={{ background: '#fff', padding: '8px 10px', cursor: 'pointer', minHeight: 56 }}>
                <Text strong style={{ fontSize: 12, color: '#020D7F' }}>
                  {c.contrato_numero || c.titulo || 'Campanha'}
                </Text>
                <div><Text type="secondary" style={{ fontSize: 10 }}>
                  {(c.profissionais || []).slice(0, 3).join(', ')}{(c.profissionais || []).length > 3 ? '…' : ''}
                </Text></div>
              </div>
              {/* faixa de dias: célula vazia + barra spanning */}
              <div style={{ gridColumn: '2 / -1', background: '#fff', position: 'relative', padding: 6 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, height: '100%' }}>
                  <div onClick={() => abrirEdicao(c)}
                    title={`${ini.format('DD/MM')} a ${fim.format('DD/MM')}`}
                    style={{ gridColumn: `${startIdx + 1} / ${endIdx + 2}`, cursor: 'pointer',
                      background: cfg.hex, color: '#fff', borderRadius: 4, padding: '6px 8px',
                      fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      alignSelf: 'center', opacity: c.status === 'cancelada' ? 0.5 : 1 }}>
                    {c.titulo || c.contrato_titulo || 'Campanha'}
                    {(c.equipamentos || []).length ? ` · 🧰 ${c.equipamentos.length}` : ''}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <CampanhaDrawer aberto={aberto} campanha={editando}
        semanaDefault={semana}
        contratos={contratos} pessoas={pessoas} equipamentos={equipamentos} kits={kitsDisp}
        onFechar={() => setAberto(false)}
        onSalvo={() => { setAberto(false); carregar(); }}
        onExcluir={(c) => modal.confirm({
          title: `Excluir a campanha "${c.titulo || c.contrato_numero || ''}"?`,
          okText: 'Excluir', okButtonProps: { danger: true },
          onOk: () => { excluir(c); setAberto(false); },
        })} />
    </div>
  );
}

function CampanhaDrawer({ aberto, campanha, semanaDefault, contratos, pessoas, equipamentos, kits, onFechar, onSalvo, onExcluir }: {
  aberto: boolean;
  campanha: Campanha | null;
  semanaDefault: Dayjs;
  contratos: Array<{ id: string; numero: string; titulo: string }>;
  pessoas: string[];
  equipamentos: Array<{ descricao: string; codigo_patrimonio: string | null; categoria: string | null; modelo: string | null }>;
  kits: KitOpcao[];
  onFechar: () => void;
  onSalvo: () => void;
  onExcluir: (c: Campanha) => void;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);
  // Arquivos + link público são geridos direto no banco (fora do form)
  const [arquivos, setArquivos] = useState<Array<{ nome: string; s3_key: string; tipo: string }>>([]);
  const [linkHash, setLinkHash] = useState<string | null>(null);
  const [uploadando, setUploadando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Arquivos "em espera" pra campanha NOVA (sobem ao salvar, quando há id)
  const [staged, setStaged] = useState<File[]>([]);

  useEffect(() => {
    if (!aberto) return;
    form.resetFields();
    setArquivos(campanha?.arquivos || []);
    setLinkHash(campanha?.token_hash || null);
    setStaged([]);
    setCopiado(false);
    if (campanha) {
      form.setFieldsValue({
        contrato_id: campanha.contrato_id || undefined,
        titulo: campanha.titulo || undefined,
        periodo: [dayjs(String(campanha.data_inicio).slice(0, 10)), dayjs(String(campanha.data_fim).slice(0, 10))],
        status: campanha.status,
        profissionais: campanha.profissionais || [],
        equipamentos: campanha.equipamentos || [],
        kits: (campanha.kits || []).map(k => k.kit_id),
        localidades: campanha.localidades || [],
        briefing: campanha.briefing || undefined,
        observacoes: campanha.observacoes || undefined,
      });
    } else {
      form.setFieldsValue({ status: 'planejada', periodo: [semanaDefault, semanaDefault.add(4, 'day')], localidades: [], kits: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, campanha?.id]);

  async function salvar(v: any) {
    setSalvando(true);
    try {
      const payload: any = {
        contrato_id: v.contrato_id || null,
        titulo: v.titulo || null,
        data_inicio: v.periodo?.[0]?.format('YYYY-MM-DD'),
        data_fim: v.periodo?.[1]?.format('YYYY-MM-DD'),
        status: v.status || 'planejada',
        profissionais: v.profissionais || [],
        equipamentos: v.equipamentos || [],
        kits_ids: v.kits || [],
        localidades: (v.localidades || []).filter((l: any) => l && (l.nome || l.link)),
        briefing: v.briefing || null,
        observacoes: v.observacoes || null,
      };
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        campanha
          ? { action: 'op_pc_atualizar', id: campanha.id, ...payload }
          : { action: 'op_pc_criar', ...payload },
        { headers: { Authorization: token } });
      const salva = r.data.body ? JSON.parse(r.data.body) : r.data;
      const id = campanha ? campanha.id : salva?.id;
      // Campanha nova: agora que existe id, sobe os KMZ que estavam em espera
      if (!campanha && id && staged.length) {
        for (const f of staged) {
          const d1 = await api('op_pc_upload_url', { id, filename: f.name, content_type: f.type || 'application/octet-stream' });
          await axios.put(d1.upload_url, f, { headers: { 'Content-Type': f.type || 'application/octet-stream' } });
          await api('op_pc_confirmar_arquivo', { id, s3_key: d1.s3_key, nome: f.name, tamanho_bytes: f.size });
        }
      }
      message.success(campanha ? 'Campanha atualizada' : 'Campanha lançada');
      form.resetFields();
      onSalvo();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function api(action: string, extra: any = {}) {
    const token = await getToken();
    const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, { action, ...extra }, { headers: { Authorization: token } });
    return r.data.body ? JSON.parse(r.data.body) : r.data;
  }
  async function subirArquivo(file: File) {
    if (!campanha) return;
    setUploadando(true);
    try {
      const d1 = await api('op_pc_upload_url', { id: campanha.id, filename: file.name, content_type: file.type || 'application/octet-stream' });
      await axios.put(d1.upload_url, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      const d2 = await api('op_pc_confirmar_arquivo', { id: campanha.id, s3_key: d1.s3_key, nome: file.name, tamanho_bytes: file.size });
      setArquivos(d2.arquivos || []);
      message.success('Arquivo anexado');
    } catch (e: any) { message.error('Falha no upload: ' + (e.response?.data?.error || e.message)); }
    finally { setUploadando(false); }
  }
  async function baixarArquivo(s3_key: string) {
    try { const d = await api('op_pc_arquivo_url', { id: campanha!.id, s3_key }); if (d.url) window.open(d.url, '_blank'); }
    catch (e: any) { message.error('Falha: ' + (e.response?.data?.error || e.message)); }
  }
  async function removerArquivo(s3_key: string) {
    try { const d = await api('op_pc_remover_arquivo', { id: campanha!.id, s3_key }); setArquivos(d.arquivos || []); }
    catch (e: any) { message.error('Falha: ' + (e.response?.data?.error || e.message)); }
  }
  async function gerarLink() {
    try { const d = await api('op_pc_gerar_link', { id: campanha!.id }); setLinkHash(d.hash); }
    catch (e: any) { message.error('Falha: ' + (e.response?.data?.error || e.message)); }
  }
  async function revogarLink() {
    try { await api('op_pc_revogar_link', { id: campanha!.id }); setLinkHash(null); }
    catch (e: any) { message.error('Falha: ' + (e.response?.data?.error || e.message)); }
  }
  const linkPublico = linkHash ? `${window.location.origin}/publico/planejamento/${linkHash}` : '';

  const equipOptions = Array.from(new Set(equipamentos.map(e => e.categoria || 'Outros'))).map(cat => ({
    label: cat,
    options: equipamentos.filter(e => (e.categoria || 'Outros') === cat).map(e => {
      const rotulo = `${e.descricao}${e.modelo ? ` ${e.modelo}` : ''}${e.codigo_patrimonio ? ` [${e.codigo_patrimonio}]` : ''}`;
      return { value: rotulo, label: rotulo };
    }),
  }));

  return (
    <Drawer open={aberto} onClose={onFechar} width={540} destroyOnClose
      title={campanha ? '✏️ Editar campanha' : 'Nova campanha de campo'}
      extra={campanha ? (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onExcluir(campanha)}>Excluir</Button>
      ) : undefined}>
      <Form form={form} layout="vertical" size="small" onFinish={salvar}>
        <Form.Item label="Contrato (centro de custo)" name="contrato_id">
          <Select showSearch optionFilterProp="label" allowClear placeholder="Buscar contrato…"
            options={contratos.map(c => ({ value: c.id, label: `${c.numero} — ${c.titulo}` }))} />
        </Form.Item>
        <Form.Item label="Título da campanha" name="titulo"
          extra="Ex: Batimetria Rio Marau — trecho 2">
          <Input placeholder="Opcional — aparece na barra do calendário" />
        </Form.Item>
        <Form.Item label="Período" name="periodo" rules={[{ required: true, message: 'Informe o período' }]}>
          <DatePicker.RangePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Equipe (profissionais)" name="profissionais"
          rules={[{ required: true, message: 'Quem vai a campo?' }]}
          extra="Usuários do sistema + profissionais de campo; pode digitar nome livre">
          <Select mode="tags" showSearch placeholder="Buscar ou digitar…"
            options={pessoas.map(p => ({ value: p, label: p }))} />
        </Form.Item>
        <Form.Item label="Kits alocados" name="kits"
          extra="Kits de equipamento que vão a campo (o app sugere a campanha no check-in)">
          <Select mode="multiple" showSearch optionFilterProp="label" allowClear
            placeholder="Buscar kit…"
            options={kits.map(k => ({ value: k.id, label: `${k.nome}${k.codigo ? ` (${k.codigo})` : ''}` }))} />
        </Form.Item>
        <Form.Item label="Equipamentos avulsos" name="equipamentos"
          extra="Itens soltos do patrimônio que não estão num kit (veículos, barcos, eletrônicos)">
          <Select mode="multiple" showSearch optionFilterProp="label" allowClear
            placeholder="Buscar equipamento…" options={equipOptions} />
        </Form.Item>

        <Form.Item label="Localidades / pontos" style={{ marginBottom: 8 }}
          extra="Pontos a visitar nesta campanha (nome + link do mapa/Google Maps)">
          <Form.List name="localidades">
            {(fields, { add, remove }) => (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {fields.map((field) => (
                  <Flex key={field.key} gap={6} align="start">
                    <EnvironmentOutlined style={{ marginTop: 8, color: '#0930D0' }} />
                    <Form.Item name={[field.name, 'nome']} style={{ flex: 1, marginBottom: 0 }}>
                      <Input placeholder="Nome do ponto" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'link']} style={{ flex: 1, marginBottom: 0 }}>
                      <Input placeholder="Link (mapa)" />
                    </Form.Item>
                    <Button size="small" type="text" danger onClick={() => remove(field.name)}>×</Button>
                  </Flex>
                ))}
                <Button size="small" type="dashed" block onClick={() => add({})}>+ Adicionar ponto</Button>
              </Space>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item label="Briefing técnico" name="briefing">
          <TextArea rows={4} placeholder="O que será feito, método, cuidados, contatos locais…" />
        </Form.Item>
        <Form.Item label="Observações" name="observacoes">
          <TextArea rows={2} placeholder="Logística, hospedagem, qualquer aviso à equipe…" />
        </Form.Item>
        <Form.Item label="Status" name="status" initialValue="planejada">
          <Select style={{ width: 180 }}
            options={Object.entries(STATUS_CFG).map(([k, v]) => ({ value: k, label: v.label }))} />
        </Form.Item>

        <Flex gap={8}>
          <Button type="primary" htmlType="submit" loading={salvando} block>
            {campanha ? 'Salvar' : 'Lançar campanha'}
          </Button>
        </Flex>
      </Form>

      {/* Arquivos: na campanha SALVA sobem na hora; na NOVA ficam em espera e
          sobem ao salvar (item: anexar KMZ já na criação). */}
      <Divider style={{ margin: '16px 0' }} />
      <Text strong style={{ fontSize: 12 }}>📎 Arquivos (KMZ / mapas)</Text>
      <div style={{ marginTop: 8, marginBottom: 12 }}>
        {campanha && arquivos.length === 0 && staged.length === 0 && (
          <Text type="secondary" style={{ fontSize: 11 }}>Nenhum arquivo anexado.</Text>
        )}
        {!campanha && staged.length === 0 && (
          <Text type="secondary" style={{ fontSize: 11 }}>Anexe o KMZ — sobe ao lançar a campanha.</Text>
        )}
        {/* já gravados (campanha existente) */}
        {arquivos.map(a => (
          <Flex key={a.s3_key} align="center" gap={6} style={{ padding: '3px 0' }}>
            <span>{a.tipo === 'kml' ? '🗺️' : a.tipo === 'pdf' ? '📄' : '📎'}</span>
            <Text style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.nome}</Text>
            <Button size="small" type="text" icon={<DownloadOutlined />} onClick={() => baixarArquivo(a.s3_key)} />
            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removerArquivo(a.s3_key)} />
          </Flex>
        ))}
        {/* em espera (campanha nova) */}
        {staged.map((f, i) => (
          <Flex key={i} align="center" gap={6} style={{ padding: '3px 0' }}>
            <span>📎</span>
            <Text style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</Text>
            <Text type="secondary" style={{ fontSize: 10 }}>em espera</Text>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => setStaged(s => s.filter((_, j) => j !== i))} />
          </Flex>
        ))}
        <input ref={fileRef} type="file" style={{ display: 'none' }}
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) { if (campanha) subirArquivo(f); else setStaged(s => [...s, f]); }
            e.target.value = '';
          }} />
        <Button size="small" icon={<UploadOutlined />} loading={uploadando}
          onClick={() => fileRef.current?.click()} style={{ marginTop: 6 }}>Anexar arquivo</Button>
      </div>

      {/* Link público só depois de salva (precisa de id) */}
      <Divider style={{ margin: '16px 0' }} />
      <Text strong style={{ fontSize: 12 }}>🔗 Link público (equipe de campo)</Text>
      {campanha ? (
        <div style={{ marginTop: 8 }}>
          {linkHash ? (
            <>
              <Space.Compact style={{ width: '100%' }}>
                <Input readOnly value={linkPublico} size="small" style={{ fontFamily: 'monospace', fontSize: 11 }} />
                <Button size="small" icon={copiado ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={() => { navigator.clipboard.writeText(linkPublico); setCopiado(true); setTimeout(() => setCopiado(false), 1500); }}>
                  {copiado ? 'Copiado' : 'Copiar'}
                </Button>
              </Space.Compact>
              <Button size="small" danger type="text" style={{ marginTop: 4 }} onClick={revogarLink}>Revogar link</Button>
            </>
          ) : (
            <Button size="small" icon={<ShareAltOutlined />} onClick={gerarLink}>Gerar link público</Button>
          )}
          <div><Text type="secondary" style={{ fontSize: 10 }}>Acesso sem login: briefing, pontos e KMZ.</Text></div>
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>Disponível depois de lançar a campanha.</Text>
        </div>
      )}
    </Drawer>
  );
}
